import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";

import { candidatosLeilao, criarLeilao, obterCotacao } from "services/leilaoFreteApi";
import { obterPerfil } from "services/gobidPerfilApi"; // PO1-3528 (épico PO1-3527)
import { soDigitos, telefoneValido } from "components/gobid/perfilEmbarcador";
import obterUserNome from "utils/userNome";
import obterUserEmail from "utils/userEmail";
import {
  agoraInputLocal,
  deInputLocal,
  diaDoInput,
  ehUrgente,
  fmtLimite,
  paraInputLocal,
  validarLimites,
} from "utils/limitesColetaEntrega"; // PO1-3481
import {
  DURACAO,
  FILTRO_CANDIDATO,
  ROTULO_REDE,
  TITULO_SELO_REDE,
  agruparCandidatos,
  cidadeUf,
  coberturaEmail,
  duracaoValida,
  ehCandidatoRede,
  emailValido,
  filtrarCandidatos,
  fmtBRL,
  fmtHora,
  fmtNumero,
  formatBRLInput,
  hojeISO,
  parseBRL,
  previsaoFechamento,
  referenciaAlvo,
  rotuloDuracao,
  temEmailCotacao,
} from "./regras";

// Criar o leilão (H3 PO1-3084): parte de uma cotação Pendente ou Stand-by,
// preço-alvo, janela (30 min padrão, 15 a 1440), data de coleta obrigatória e
// as convidadas sugeridas pela H5 (carteira + Sourcing do destino). Publica em
// um passo: o leilão nasce aberto e os convites saem na hora.
//
// Revisto em 13/10/2026 no molde do detalhe do leilão (cabeçalho, carga em
// chips, linhas-cartão), com o ALERTA de cobertura de e-mail de Cotação: sem
// contato de Cotação a transportadora só entra se o usuário digitar um, e o
// backend grava esse e-mail no cadastro da transportadora (P16 = C).

function Selo({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone}`} title={title}>
      {children}
    </span>
  );
}

// PO1-3528: contato que vai no bloco "Quem está contratando" do convite das
// convidadas de FORA da carteira. 'perfil' = o contato padrão do Perfil do
// embarcador (ou quem está criando, sem perfil); 'outro' = só nesta oferta.
const CONTATO_MODO = { perfil: "perfil", outro: "outro" };
const CONTATO_VAZIO = { nome: "", email: "", telefone: "", whatsapp: "" };

// Valida o contato da oferta como o backend: nome e e-mail obrigatórios,
// telefone e WhatsApp com 10 a 13 dígitos quando informados.
export function validarContatoOferta(c) {
  const e = {};
  if (!String(c.nome || "").trim()) e.nome = "Informe o nome do contato.";
  if (!emailValido(c.email)) e.email = "Informe um e-mail válido.";
  if (!telefoneValido(c.telefone)) e.telefone = "Telefone com 10 a 13 dígitos.";
  if (!telefoneValido(c.whatsapp)) e.whatsapp = "WhatsApp com 10 a 13 dígitos.";
  return e;
}

// O que a opção "Usar o contato do perfil" mostra: o contato padrão do perfil
// quando ele existe e está liberado; senão, quem está criando o leilão.
export function contatoPadraoDoPerfil(r) {
  const p = r && r.perfil;
  if (p && p.contatoNome && p.mostrarContatoNome !== false) {
    return { origem: "perfil", nome: p.contatoNome, email: p.mostrarContatoEmail !== false ? p.contatoEmail || "" : "" };
  }
  const cp = r && r.contatoPadrao;
  return { origem: "voce", nome: (cp && cp.nome) || obterUserNome() || "", email: (cp && cp.email) || obterUserEmail() || "" };
}

const FILTROS = [
  { chave: FILTRO_CANDIDATO.todas, rotulo: "Todas" },
  { chave: FILTRO_CANDIDATO.carteira, rotulo: "Carteira" },
  { chave: FILTRO_CANDIDATO.comEmail, rotulo: "Com e-mail" },
  { chave: FILTRO_CANDIDATO.semEmail, rotulo: "Sem e-mail" },
];

export default function NovoLeilaoModal({ isOpen, onClose, empresaId, embarqueId, onCriado, onJaAtivo }) {
  const [loading, setLoading] = useState(false);
  const [erroCarga, setErroCarga] = useState(null);
  const [dados, setDados] = useState(null);
  const [precoAlvo, setPrecoAlvo] = useState("");
  const [duracaoMin, setDuracaoMin] = useState(String(DURACAO.padrao));
  const [outraDuracao, setOutraDuracao] = useState(false);
  const [dataColeta, setDataColeta] = useState("");
  const [observacoes, setObservacoes] = useState("");
  // PO1-3481: limites de coleta/entrega (valor do input datetime-local).
  const [coletarAte, setColetarAte] = useState("");
  const [entregarAte, setEntregarAte] = useState("");
  const [selecionados, setSelecionados] = useState({});
  const [emailsDigitados, setEmailsDigitados] = useState({});
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState(FILTRO_CANDIDATO.todas);
  const [erros, setErros] = useState({});
  const [publicando, setPublicando] = useState(false);
  const [erroGeral, setErroGeral] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  const [cotacoesRef, setCotacoesRef] = useState([]);
  // PO1-3528: contato desta oferta.
  const [contatoModo, setContatoModo] = useState(CONTATO_MODO.perfil);
  const [contato, setContato] = useState(CONTATO_VAZIO);
  const [perfilContato, setPerfilContato] = useState(null);

  // Carrega a cotação + candidatos ao abrir.
  useEffect(() => {
    if (!isOpen || !empresaId || !embarqueId) return undefined;
    let ativo = true;
    setLoading(true);
    setErroCarga(null);
    setDados(null);
    setErros({});
    setErroGeral(null);
    setPrecoAlvo("");
    setDuracaoMin(String(DURACAO.padrao));
    setOutraDuracao(false);
    setDataColeta(hojeISO());
    setObservacoes("");
    setColetarAte("");
    setEntregarAte("");
    setEmailsDigitados({});
    setBusca("");
    setFiltro(FILTRO_CANDIDATO.todas);
    setCotacoesRef([]);
    setContatoModo(CONTATO_MODO.perfil);
    setContato(CONTATO_VAZIO);
    setPerfilContato(null);
    // Contato padrão do perfil: se falhar, mostra só "Você".
    obterPerfil(empresaId)
      .then((r) => {
        if (ativo) setPerfilContato(contatoPadraoDoPerfil(r));
      })
      .catch(() => {
        if (ativo) setPerfilContato(contatoPadraoDoPerfil(null));
      });
    candidatosLeilao(empresaId, embarqueId)
      .then((r) => {
        if (!ativo) return;
        setDados(r);
        // Observações da cotação já entram no campo: o embarcador vê o que vai
        // para as transportadoras e pode ajustar antes de publicar.
        setObservacoes((r.embarque && r.embarque.observacoes) || "");
        // Limites da cotação entram só se ainda estão no futuro.
        const noFuturo = (v) => (v && new Date(v).getTime() > Date.now() ? paraInputLocal(v) : "");
        setColetarAte(noFuturo(r.embarque && r.embarque.coletarAte));
        setEntregarAte(noFuturo(r.embarque && r.embarque.entregarAte));
        const pre = {};
        // Pré-seleção: carteira que atende o destino E já tem e-mail de Cotação
        // (sem e-mail, marcar de saída travaria o "Publicar" até digitar um).
        // Candidato da Rede Everlog (PO1-3648) nunca entra de saída: a
        // cobertura é declarada, o embarcador decide se convida.
        (r.candidatos || []).forEach((c) => {
          if (c.naCarteira && c.atendeDestino && temEmailCotacao(c) && !ehCandidatoRede(c)) pre[c.idTransportadora] = true;
        });
        setSelecionados(pre);
        // Alvo NUNCA pré-preenchido (13/10/2026): o frete pode vir de tabela
        // errada. Sem frete na cotação (leilão direto), a referência é a menor
        // proposta da simulação, se houver: busca a cotação completa.
        if (!(r.embarque && r.embarque.valor_total > 0)) {
          obterCotacao(embarqueId)
            .then((doc) => {
              if (ativo) setCotacoesRef((doc && doc.cotacoes) || []);
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        if (!ativo) return;
        const data = (err.response && err.response.data) || {};
        setErroCarga(data.error || "Não foi possível carregar a cotação.");
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [isOpen, empresaId, embarqueId]);

  // Relógio só para a previsão "fecha por volta das HH:MM".
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, [isOpen]);

  const candidatos = (dados && dados.candidatos) || [];
  const embarque = dados && dados.embarque;
  const maximo = (dados && dados.maximoConvidados) || 50;
  const idsSelecionados = useMemo(() => Object.keys(selecionados).filter((k) => selecionados[k]).map(Number), [selecionados]);
  const cobertura = useMemo(() => coberturaEmail(candidatos), [candidatos]);
  const visiveis = useMemo(() => filtrarCandidatos(candidatos, busca, filtro), [candidatos, busca, filtro]);
  const grupos = useMemo(() => agruparCandidatos(visiveis), [visiveis]);
  const contagemFiltro = useMemo(() => {
    const r = {};
    FILTROS.forEach((f) => {
      r[f.chave] = filtrarCandidatos(candidatos, "", f.chave).length;
    });
    return r;
  }, [candidatos]);

  const semEmail = useMemo(
    () => candidatos.filter((c) => selecionados[c.idTransportadora] && !temEmailCotacao(c) && !emailValido(emailsDigitados[c.idTransportadora])),
    [candidatos, selecionados, emailsDigitados],
  );

  const fecha = previsaoFechamento(duracaoMin, agora);
  const alvoNum = parseBRL(precoAlvo);
  const freteAtual = embarque && embarque.valor_total > 0 ? embarque.valor_total : null;
  const referencia = referenciaAlvo(embarque, cotacoesRef);

  const toggle = (id) => setSelecionados((cur) => ({ ...cur, [id]: !cur[id] }));
  const selecionarVisiveisComEmail = () =>
    setSelecionados((cur) => {
      const novo = { ...cur };
      visiveis.forEach((c) => {
        if (temEmailCotacao(c)) novo[c.idTransportadora] = true;
      });
      return novo;
    });
  const limparSelecao = () => setSelecionados({});

  const validar = useCallback(() => {
    const e = {};
    if (!(alvoNum > 0)) e.precoAlvo = "Informe o preço-alvo.";
    if (!duracaoValida(duracaoMin)) e.duracaoMin = `A janela vai de ${DURACAO.min} a ${DURACAO.max} minutos.`;
    Object.assign(e, validarLimites({ coletarAte, entregarAte }));
    if (coletarAte && !e.coletarAte && !e.duracaoMin && fecha && fecha.getTime() > new Date(coletarAte).getTime()) {
      e.duracaoMin = `O leilão fecharia depois do limite de coleta (${fmtLimite(coletarAte)}). Diminua a duração.`;
    }
    if (!coletarAte) {
      if (!dataColeta) e.dataColeta = "Informe a data de coleta.";
      else if (dataColeta < hojeISO()) e.dataColeta = "A coleta não pode ser anterior a hoje.";
    }
    if (!idsSelecionados.length) e.convidados = "Convide pelo menos uma transportadora.";
    else if (idsSelecionados.length > maximo) e.convidados = `Máximo de ${maximo} convidadas.`;
    else if (semEmail.length) e.convidados = `Informe o e-mail de: ${semEmail.map((c) => c.nome).join(", ")}.`;
    if (contatoModo === CONTATO_MODO.outro) {
      const ec = validarContatoOferta(contato);
      if (Object.keys(ec).length) e.contato = ec;
    }
    setErros(e);
    return Object.keys(e).length === 0;
  }, [alvoNum, duracaoMin, dataColeta, coletarAte, entregarAte, fecha, idsSelecionados, semEmail, maximo, contatoModo, contato]);

  const publicar = async () => {
    if (!validar() || publicando) return;
    setPublicando(true);
    setErroGeral(null);
    try {
      const convidados = idsSelecionados.map((id) => {
        const digitado = String(emailsDigitados[id] || "").trim();
        return digitado ? { idTransportadora: id, email: digitado } : { idTransportadora: id };
      });
      const criado = await criarLeilao(empresaId, {
        embarqueId,
        precoAlvo: alvoNum,
        duracaoMin: Number(duracaoMin),
        dataColeta: coletarAte ? diaDoInput(coletarAte) : dataColeta,
        coletarAte: deInputLocal(coletarAte),
        entregarAte: deInputLocal(entregarAte),
        observacoes,
        convidados,
        userNome: obterUserNome(),
        // PO1-3528: só na opção "Outro contato"; sem ele o backend usa o perfil.
        contato:
          contatoModo === CONTATO_MODO.outro
            ? { nome: contato.nome.trim(), email: contato.email.trim(), telefone: soDigitos(contato.telefone) || undefined, whatsapp: soDigitos(contato.whatsapp) || undefined }
            : undefined,
      });
      if (onCriado) onCriado(criado);
    } catch (err) {
      const status = err.response && err.response.status;
      const data = (err.response && err.response.data) || {};
      if (status === 409 && data.code === "leilao_ja_ativo" && onJaAtivo) {
        onJaAtivo(data.leilaoId, data.error);
        return;
      }
      if (status === 400 && data.errors) {
        setErros(data.errors);
        if (data.convidadosSemEmail) {
          const nomes = candidatos.filter((c) => data.convidadosSemEmail.indexOf(c.idTransportadora) !== -1).map((c) => c.nome);
          setErros((cur) => ({ ...cur, convidados: `Informe o e-mail de: ${nomes.join(", ")}.` }));
        }
      }
      setErroGeral(data.error || "Não foi possível publicar o leilão.");
    } finally {
      setPublicando(false);
    }
  };

  const destinoTxt = dados && cidadeUf(dados.destino) !== "—" ? cidadeUf(dados.destino) : embarque ? cidadeUf({ cidade: embarque.cidadeDestino, uf: embarque.ufDestino }) : "—";

  const linhaCandidato = (c) => {
    const sel = Boolean(selecionados[c.idTransportadora]);
    const temEmail = temEmailCotacao(c);
    const digitado = emailsDigitados[c.idTransportadora] || "";
    const digitadoOk = emailValido(digitado);
    return (
      <div key={c.idTransportadora} className={`lf-row lf-cand${sel ? " is-selected" : ""}`}>
        <label className="lf-cand-main">
          <input type="checkbox" className="lf-cand-check" checked={sel} onChange={() => toggle(c.idTransportadora)} />
          <span className="lf-row-main">
            <span className="lf-row-nome">
              {c.nome}
              {c.naCarteira ? <Selo tone="info">Carteira</Selo> : <Selo tone="neutral" title="Encontrada pela Sourcing; sem vínculo com você">Fora da carteira</Selo>}
              {ehCandidatoRede(c) ? <Selo tone="warning" title={TITULO_SELO_REDE}>{ROTULO_REDE}</Selo> : null}
              {c.atendeDestino && !ehCandidatoRede(c) ? <Selo tone="success" title="Entrega neste destino (histórico de CT-e)">Atende o destino</Selo> : null}
            </span>
            <span className="lf-cand-sub">
              {c.cnpj ? <span>{c.cnpj}</span> : null}
              {temEmail ? (
                <span className="lf-cand-email">{c.emails.join(", ")}</span>
              ) : sel && digitadoOk ? (
                <span className="lf-text-success">Novo contato de Cotação</span>
              ) : (
                <span className="lf-text-warning">Sem e-mail de Cotação</span>
              )}
            </span>
          </span>
        </label>
        {!temEmail && sel ? (
          <div className="lf-cand-digitar">
            <Input
              bsSize="sm"
              type="email"
              placeholder="E-mail de cotação da transportadora"
              value={digitado}
              invalid={Boolean(digitado) && !digitadoOk}
              onChange={(e) => {
                // React 16 recicla o evento: ler o valor ANTES do updater (senão tela preta).
                const valor = e.target.value;
                setEmailsDigitados((cur) => ({ ...cur, [c.idTransportadora]: valor }));
              }}
            />
            <div className="lf-muted">Fica salvo no cadastro da transportadora como contato de Cotação.</div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top" contentClassName="lf-modal" backdrop="static" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>Novo leilão</span>
          {embarque ? (
            <span className="lc-badge lc-badge-cotacao" title="Cotação de origem">
              Cotação {embarque.numeroCotacao != null ? `nº ${embarque.numeroCotacao}` : embarque.baseNumEmbarque}
            </span>
          ) : null}
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Carregando a cotação e as transportadoras...
          </div>
        ) : erroCarga ? (
          <div className="lf-aviso lf-aviso-danger">{erroCarga}</div>
        ) : embarque ? (
          <>
            {!embarque.podeLeiloar ? (
              <div className="lf-aviso lf-aviso-warning">
                Só cotação Pendente ou Stand-by, na versão mais recente, pode ir a leilão. Esta está em {embarque.statusEmbarque}.
              </div>
            ) : null}

            {/* Carga */}
            <div className="lf-section">
              <div className="lf-carga">
                <div className="lf-rota">
                  {embarque.cliente || "Cliente"} <span className="lf-rota-seta">→</span> {destinoTxt}
                </div>
                <div className="lf-chips">
                  {embarque.pesoBruto ? <span className="lf-chip">{fmtNumero(embarque.pesoBruto)} kg</span> : null}
                  {embarque.volume ? <span className="lf-chip">{fmtNumero(embarque.volume, 3)} m³</span> : null}
                  <span className="lf-chip">NF {fmtBRL(embarque.valorNF)}</span>
                  {freteAtual ? (
                    <span className="lf-chip" title="Frete da cotação hoje">
                      Frete atual {fmtBRL(freteAtual)}
                      {embarque.transportadora ? ` · ${embarque.transportadora}` : ""}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Regras do leilão */}
            <div className="lf-config">
              <div className="lf-kpi">
                <div className="lf-kpi-label">Preço-alvo (R$)</div>
                <Input
                  className="lf-input-alvo"
                  inputMode="decimal"
                  value={precoAlvo}
                  placeholder="0,00"
                  onChange={(e) => setPrecoAlvo(formatBRLInput(e.target.value))}
                  invalid={Boolean(erros.precoAlvo)}
                />
                {erros.precoAlvo ? <div className="lf-erro-campo">{erros.precoAlvo}</div> : null}
                <div className="lf-kpi-sub">
                  Visível para as convidadas. Elas aceitam o alvo ou propõem menos.
                </div>
                {referencia ? (
                  <div className="lf-ref-alvo">
                    <div>
                      <span className="lf-ref-rotulo">{referencia.origem === "frete" ? "Frete atual da cotação" : "Menor proposta da simulação"}:</span>{" "}
                      <b>{fmtBRL(referencia.valor)}</b>
                      {referencia.transportadora ? <span className="lf-muted"> · {referencia.transportadora}</span> : null}
                    </div>
                    <div className="lf-muted">Só referência: pode vir de uma tabela desatualizada. Confira antes de usar.</div>
                    {alvoNum > 0 && Math.abs(alvoNum - referencia.valor) >= 0.01 ? (
                      <div className={alvoNum < referencia.valor ? "lf-text-success" : "lf-text-warning"}>
                        Alvo {alvoNum < referencia.valor ? "abaixo" : "acima"} da referência em {fmtBRL(Math.abs(alvoNum - referencia.valor))}.
                      </div>
                    ) : null}
                    {!(alvoNum > 0) ? (
                      <button type="button" className="lf-link" onClick={() => setPrecoAlvo(formatBRLInput(referencia.valor))}>
                        usar este valor
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="lf-kpi">
                <div className="lf-kpi-label">Janela de lances</div>
                <div className="lf-presets">
                  {DURACAO.presets.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`lf-preset${!outraDuracao && String(duracaoMin) === String(p) ? " is-active" : ""}`}
                      onClick={() => {
                        setOutraDuracao(false);
                        setDuracaoMin(String(p));
                      }}
                    >
                      {rotuloDuracao(p)}
                    </button>
                  ))}
                  <button type="button" className={`lf-preset${outraDuracao ? " is-active" : ""}`} onClick={() => setOutraDuracao(true)}>
                    Outra
                  </button>
                </div>
                {outraDuracao ? (
                  <div className="lf-duracao-outra">
                    <Input
                      bsSize="sm"
                      type="number"
                      min={DURACAO.min}
                      max={DURACAO.max}
                      value={duracaoMin}
                      onChange={(e) => setDuracaoMin(e.target.value)}
                      invalid={Boolean(erros.duracaoMin)}
                    />
                    <span className="lf-muted">minutos (15 a 1440)</span>
                  </div>
                ) : null}
                {erros.duracaoMin ? <div className="lf-erro-campo">{erros.duracaoMin}</div> : null}
                <div className="lf-kpi-sub">{fecha ? `Publicando agora, fecha por volta das ${fmtHora(fecha)}.` : "Informe de 15 a 1440 minutos."}</div>
              </div>

              <div className="lf-kpi">
                <div className="lf-kpi-label">Coletar até</div>
                <Input
                  id="leilao-coletar-ate"
                  type="datetime-local"
                  min={agoraInputLocal()}
                  value={coletarAte}
                  onChange={(e) => setColetarAte(e.target.value)}
                  invalid={Boolean(erros.coletarAte)}
                />
                {erros.coletarAte ? <div className="lf-erro-campo">{erros.coletarAte}</div> : null}
                {coletarAte && ehUrgente(coletarAte) && !erros.coletarAte ? (
                  <div className="lf-urgente-aviso">Urgente: o convite sai marcado como urgente.</div>
                ) : (
                  <div className="lf-kpi-sub">Opcional. Com horário, o leilão precisa fechar antes dele.</div>
                )}
              </div>

              <div className="lf-kpi">
                <div className="lf-kpi-label">Entregar até</div>
                <Input
                  id="leilao-entregar-ate"
                  type="datetime-local"
                  min={coletarAte || agoraInputLocal()}
                  value={entregarAte}
                  onChange={(e) => setEntregarAte(e.target.value)}
                  invalid={Boolean(erros.entregarAte)}
                />
                {erros.entregarAte ? <div className="lf-erro-campo">{erros.entregarAte}</div> : null}
                <div className="lf-kpi-sub">Opcional. Vai no convite e na página do lance.</div>
              </div>

              <div className="lf-kpi">
                <div className="lf-kpi-label">Data de coleta</div>
                <Input
                  type="date"
                  min={hojeISO()}
                  value={coletarAte ? diaDoInput(coletarAte) : dataColeta}
                  onChange={(e) => setDataColeta(e.target.value)}
                  disabled={Boolean(coletarAte)}
                  invalid={Boolean(erros.dataColeta)}
                />
                {erros.dataColeta ? <div className="lf-erro-campo">{erros.dataColeta}</div> : null}
                <div className="lf-kpi-sub">
                  {coletarAte ? "Vem do \"Coletar até\"." : "Vai no convite, para a transportadora saber quando a carga sai."}
                </div>
              </div>
            </div>

            {/* Observações: vão no convite, na página do lance e no detalhe */}
            <div className="lf-section">
              <div className="lf-section-head">
                <div className="lf-section-title">Observações para as transportadoras</div>
              </div>
              <Input
                type="textarea"
                rows={3}
                maxLength={2000}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Ex.: carga fracionada, medidas dos volumes, horário de coleta, restrição de entrega..."
                invalid={Boolean(erros.observacoes)}
              />
              {erros.observacoes ? <div className="lf-erro-campo">{erros.observacoes}</div> : null}
              <div className="lf-kpi-sub">Vai em destaque no convite e na página do lance. Veio da cotação; ajuste se precisar.</div>
            </div>

            {/* PO1-3528: contato desta oferta (só quem é de fora da carteira vê) */}
            <div className="lf-section" data-testid="lf-contato-oferta">
              <div className="lf-section-head">
                <div className="lf-section-title">Contato desta oferta</div>
              </div>
              <div className="lf-muted" style={{ marginBottom: 8 }}>
                Vai no bloco "Quem está contratando" do convite das transportadoras de fora da carteira. Quem já é da carteira não recebe.
              </div>
              <div className="lf-rows">
                <label className={`lf-row lf-contato-opcao${contatoModo === CONTATO_MODO.perfil ? " is-selected" : ""}`} style={{ cursor: "pointer" }}>
                  <input type="radio" name="contato-modo" className="lf-cand-check" checked={contatoModo === CONTATO_MODO.perfil} onChange={() => setContatoModo(CONTATO_MODO.perfil)} />
                  <span className="lf-row-main">
                    <span className="lf-row-nome">Usar o contato do perfil</span>
                    <span className="lf-cand-sub">
                      {!perfilContato ? (
                        <span className="lf-muted">Carregando...</span>
                      ) : perfilContato.origem === "perfil" ? (
                        <span>
                          {perfilContato.nome}
                          {perfilContato.email ? ` · ${perfilContato.email}` : ""}
                        </span>
                      ) : (
                        <span>
                          Você ({[perfilContato.nome, perfilContato.email].filter(Boolean).join(", ") || "sem perfil configurado"})
                        </span>
                      )}
                    </span>
                  </span>
                </label>
                <label className={`lf-row lf-contato-opcao${contatoModo === CONTATO_MODO.outro ? " is-selected" : ""}`} style={{ cursor: "pointer" }}>
                  <input type="radio" name="contato-modo" className="lf-cand-check" checked={contatoModo === CONTATO_MODO.outro} onChange={() => setContatoModo(CONTATO_MODO.outro)} />
                  <span className="lf-row-main">
                    <span className="lf-row-nome">Outro contato só para esta oferta</span>
                    <span className="lf-cand-sub">Nome e e-mail obrigatórios; telefone e WhatsApp opcionais.</span>
                  </span>
                </label>
              </div>
              {contatoModo === CONTATO_MODO.outro ? (
                <div className="lf-cand-digitar" style={{ marginTop: 8 }}>
                  <div className="bf-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px 12px" }}>
                    {[
                      { campo: "nome", rotulo: "Nome *", tipo: "text", max: 120 },
                      { campo: "email", rotulo: "E-mail *", tipo: "email", max: 160 },
                      { campo: "telefone", rotulo: "Telefone", tipo: "tel", max: 20 },
                      { campo: "whatsapp", rotulo: "WhatsApp", tipo: "tel", max: 20 },
                    ].map((c) => (
                      <div key={c.campo}>
                        <div className="lf-kpi-label">{c.rotulo}</div>
                        <Input
                          bsSize="sm"
                          type={c.tipo}
                          maxLength={c.max}
                          aria-label={`Contato ${c.rotulo.replace(" *", "")}`}
                          value={contato[c.campo]}
                          invalid={Boolean(erros.contato && erros.contato[c.campo])}
                          placeholder={c.tipo === "tel" ? "Só números, com DDD" : undefined}
                          onChange={(e) => {
                            // React 16 recicla o evento: ler o valor ANTES do updater.
                            const valor = c.tipo === "tel" ? soDigitos(e.target.value) : e.target.value;
                            setContato((cur) => ({ ...cur, [c.campo]: valor }));
                          }}
                        />
                        {erros.contato && erros.contato[c.campo] ? <div className="lf-erro-campo">{erros.contato[c.campo]}</div> : null}
                      </div>
                    ))}
                  </div>
                  <div className="lf-text-warning" style={{ marginTop: 6 }}>
                    Telefone e WhatsApp digitados aqui aparecem para as convidadas de fora da carteira, inclusive concorrentes entre si.
                  </div>
                </div>
              ) : null}
            </div>

            {/* Convidadas */}
            <div className="lf-section">
              <div className="lf-section-head">
                <div className="lf-section-title">
                  Convidadas · {idsSelecionados.length} de {maximo}
                </div>
                <div className="lf-acoes-lista">
                  <button type="button" className="lf-link" onClick={selecionarVisiveisComEmail}>
                    marcar as com e-mail
                  </button>
                  {idsSelecionados.length ? (
                    <button type="button" className="lf-link" onClick={limparSelecao}>
                      limpar
                    </button>
                  ) : null}
                </div>
              </div>

              {cobertura.semEmail > 0 ? (
                <div className="lf-banner lf-banner-alerta">
                  <strong>
                    {cobertura.semEmail} de {cobertura.total} transportadoras estão sem e-mail de Cotação
                    {cobertura.carteiraSemEmail ? ` (${cobertura.carteiraSemEmail} da sua carteira)` : ""}.
                  </strong>
                  <span>
                    Sem e-mail, o convite não sai. Marque a transportadora e digite o e-mail: ele fica salvo no cadastro dela para os próximos leilões. Para acertar
                    vários de uma vez, fale com o seu consultor Everlog.
                  </span>
                </div>
              ) : null}
              {!dados.sourcingDisponivel ? (
                <div className="lf-aviso lf-aviso-info">A cidade de destino não foi encontrada no cadastro; só a sua carteira aparece aqui.</div>
              ) : null}
              {erros.convidados ? <div className="lf-aviso lf-aviso-danger">{erros.convidados}</div> : null}

              {candidatos.length === 0 ? (
                <div className="lf-empty-state">Nenhuma transportadora na carteira nem na Sourcing para este destino.</div>
              ) : (
                <>
                  <div className="lf-toolbar">
                    <Input bsSize="sm" className="lf-busca" placeholder="Buscar por nome, CNPJ ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} />
                    <div className="lf-presets lf-filtros">
                      {FILTROS.map((f) => (
                        <button key={f.chave} type="button" className={`lf-preset${filtro === f.chave ? " is-active" : ""}`} onClick={() => setFiltro(f.chave)}>
                          {f.rotulo} <span className="lf-preset-n">{contagemFiltro[f.chave]}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="lf-cands">
                    {grupos.length === 0 ? <div className="lf-vazio">Nenhuma transportadora com esse filtro.</div> : null}
                    {grupos.map((g) => (
                      <div key={g.chave} className="lf-grupo">
                        <div className="lf-grupo-titulo">
                          {g.titulo} <span className="lf-muted">({g.itens.length})</span>
                        </div>
                        <div className="lf-rows">{g.itens.map(linhaCandidato)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {erroGeral ? <div className="lf-aviso lf-aviso-danger">{erroGeral}</div> : null}
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {embarque ? (
          <div className="lf-confirma lf-resumo-rodape">
            {idsSelecionados.length} convidada{idsSelecionados.length === 1 ? "" : "s"}
            {semEmail.length ? <span className="lf-text-warning"> · {semEmail.length} sem e-mail</span> : null}
            {alvoNum > 0 ? ` · alvo ${fmtBRL(alvoNum)}` : ""}
            {duracaoValida(duracaoMin) ? ` · ${rotuloDuracao(duracaoMin)}` : ""}
          </div>
        ) : null}
        <Button className="lc-btn-cancel" onClick={onClose} disabled={publicando}>
          Cancelar
        </Button>
        <Button color="primary" onClick={publicar} disabled={publicando || loading || !embarque || !embarque.podeLeiloar}>
          {publicando ? "Publicando..." : "Publicar leilão"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
